// WP-1.13 (OB-2, S-38 `/import`): 4-step wizard — upload CSV → column mapping (saved per
// source name) → preview with dedupe (TX-6) + error flags → commit (backfill toggle, OB-3).
// The file never leaves the browser until commit — only parsed rows cross the wire, and only
// for the account/columns the owner explicitly mapped.
import type { ImportColumnMapping, ImportPreviewRow } from '@pfos/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountPicker } from '../components/AccountPicker';
import { Amount } from '../components/Amount';
import {
  checkboxClass,
  Field,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import { ApiError } from '../lib/api';
import { parseCsv } from '../lib/csv';
import {
  useCommitCsvImport,
  useCreateImportSource,
  useImportSources,
  usePreviewCsvImport,
} from '../lib/import';

interface MappingDraft {
  date: string;
  amount: string;
  category: string;
  party: string;
  note: string;
}
const EMPTY_MAPPING: MappingDraft = { date: '', amount: '', category: '', party: '', note: '' };

function toColumnMapping(draft: MappingDraft): ImportColumnMapping {
  return {
    date: draft.date,
    amount: draft.amount,
    ...(draft.category !== '' ? { category: draft.category } : {}),
    ...(draft.party !== '' ? { party: draft.party } : {}),
    ...(draft.note !== '' ? { note: draft.note } : {}),
  };
}

function ColumnSelect({
  label,
  htmlFor,
  headers,
  value,
  onChange,
}: {
  label: string;
  htmlFor: string;
  headers: string[];
  value: string;
  onChange: (v: string) => void;
}): React.JSX.Element {
  return (
    <Field label={label} htmlFor={htmlFor}>
      <select
        id={htmlFor}
        className={selectClass}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        <option value="">—</option>
        {headers.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function ImportPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { data: sources } = useImportSources();
  const createSource = useCreateImportSource();
  const previewMutation = usePreviewCsvImport();
  const commitMutation = useCommitCsvImport();

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [headers, setHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [accountId, setAccountId] = useState('');
  const [mapping, setMapping] = useState<MappingDraft>(EMPTY_MAPPING);
  const [sourceName, setSourceName] = useState('');
  const [preview, setPreview] = useState<ImportPreviewRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [backfill, setBackfill] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mappingComplete = accountId !== '' && mapping.date !== '' && mapping.amount !== '';

  const readFileText = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(typeof reader.result === 'string' ? reader.result : '');
      };
      reader.onerror = () => {
        reject(new Error('Could not read the file'));
      };
      reader.readAsText(file);
    });

  const onFileChange = async (file: File): Promise<void> => {
    setError(null);
    const text = await readFileText(file);
    const parsed = parseCsv(text);
    if (parsed.rows.length === 0) {
      setError('That file has no data rows.');
      return;
    }
    setHeaders(parsed.headers);
    setCsvRows(parsed.rows);
    setStep(2);
  };

  const runPreview = (): void => {
    setError(null);
    previewMutation.mutate(
      { accountId, mapping: toColumnMapping(mapping), rows: csvRows },
      {
        onSuccess: (rows) => {
          setPreview(rows);
          setSelected(
            new Set(
              rows
                .filter(
                  (r): r is Extract<ImportPreviewRow, { ok: true }> =>
                    r.ok && r.duplicateOfTransactionId === undefined,
                )
                .map((r) => r.rowIndex),
            ),
          );
          setStep(3);
        },
        onError: (e) => {
          setError(e instanceof ApiError ? e.message : 'Could not preview this file');
        },
      },
    );
  };

  const okRows = preview.filter((r): r is Extract<ImportPreviewRow, { ok: true }> => r.ok);
  const selectedRows = okRows.filter((r) => selected.has(r.rowIndex));

  const runCommit = (): void => {
    setError(null);
    commitMutation.mutate(
      {
        accountId,
        ...(backfill ? { backfill: true } : {}),
        rows: selectedRows.map((r) => ({
          type: r.type,
          amountMinor: r.amountMinor,
          at: r.at,
          ...(r.category !== undefined ? { category: r.category } : {}),
          ...(r.party !== undefined ? { party: r.party } : {}),
          ...(r.note !== undefined ? { note: r.note } : {}),
        })),
      },
      {
        onSuccess: () => {
          setStep(4);
        },
        onError: (e) => {
          setError(e instanceof ApiError ? e.message : 'Could not import these rows');
        },
      },
    );
  };

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold text-ink">Import</h1>

      {step === 1 && (
        <div className="max-w-md rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <Field label="Upload CSV" htmlFor="import-file">
            <input
              id="import-file"
              type="file"
              accept=".csv,text/csv"
              className={inputClass}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file !== undefined) void onFileChange(file);
              }}
            />
          </Field>
          <p className="text-xs text-muted">
            One column for the date, one signed amount column (negative = expense, positive =
            income).
          </p>
        </div>
      )}

      {step === 2 && (
        <div className="max-w-md rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <h2 className="mb-3 text-sm font-medium text-ink">Column mapping</h2>

          {sources !== undefined && sources.length > 0 && (
            <Field label="Saved source" htmlFor="import-source">
              <select
                id="import-source"
                className={selectClass}
                defaultValue=""
                onChange={(e) => {
                  const found = sources.find((s) => s.id === e.target.value);
                  if (found === undefined) return;
                  setSourceName(found.name);
                  setMapping({
                    date: found.mapping.date,
                    amount: found.mapping.amount,
                    category: found.mapping.category ?? '',
                    party: found.mapping.party ?? '',
                    note: found.mapping.note ?? '',
                  });
                }}
              >
                <option value="">Choose a saved mapping…</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Account" htmlFor="import-account">
            <AccountPicker id="import-account" value={accountId} onChange={setAccountId} />
          </Field>

          <ColumnSelect
            label="Date column"
            htmlFor="import-map-date"
            headers={headers}
            value={mapping.date}
            onChange={(v) => {
              setMapping((m) => ({ ...m, date: v }));
            }}
          />
          <ColumnSelect
            label="Amount column"
            htmlFor="import-map-amount"
            headers={headers}
            value={mapping.amount}
            onChange={(v) => {
              setMapping((m) => ({ ...m, amount: v }));
            }}
          />
          <ColumnSelect
            label="Category column (optional)"
            htmlFor="import-map-category"
            headers={headers}
            value={mapping.category}
            onChange={(v) => {
              setMapping((m) => ({ ...m, category: v }));
            }}
          />
          <ColumnSelect
            label="Party column (optional)"
            htmlFor="import-map-party"
            headers={headers}
            value={mapping.party}
            onChange={(v) => {
              setMapping((m) => ({ ...m, party: v }));
            }}
          />
          <ColumnSelect
            label="Note column (optional)"
            htmlFor="import-map-note"
            headers={headers}
            value={mapping.note}
            onChange={(v) => {
              setMapping((m) => ({ ...m, note: v }));
            }}
          />

          <div className="mt-3 flex items-center gap-2">
            <input
              id="import-save-source"
              className={inputClass}
              placeholder="Save this mapping as… (optional)"
              value={sourceName}
              onChange={(e) => {
                setSourceName(e.target.value);
              }}
            />
            <button
              type="button"
              className={primaryButtonInlineClass}
              disabled={
                sourceName.trim() === '' ||
                mapping.date === '' ||
                mapping.amount === '' ||
                createSource.isPending
              }
              onClick={() => {
                createSource.mutate({ name: sourceName.trim(), mapping: toColumnMapping(mapping) });
              }}
            >
              Save
            </button>
          </div>

          <button
            type="button"
            className={`${primaryButtonInlineClass} mt-4`}
            disabled={!mappingComplete || previewMutation.isPending}
            onClick={runPreview}
          >
            Preview
          </button>
          {error !== null && <p className="mt-2 text-sm text-danger">{error}</p>}
        </div>
      )}

      {step === 3 && (
        <div>
          <h2 className="mb-3 text-sm font-medium text-ink">
            Preview — {okRows.length} row{okRows.length === 1 ? '' : 's'} parsed
            {preview.length > okRows.length
              ? `, ${String(preview.length - okRows.length)} error(s)`
              : ''}
          </h2>
          <ul className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface px-4">
            {preview.map((row) => (
              <li
                key={row.rowIndex}
                className="flex items-center justify-between gap-2 border-b border-border py-2 last:border-0"
              >
                {row.ok ? (
                  <>
                    <label className="flex flex-1 items-center gap-2">
                      <input
                        type="checkbox"
                        className={checkboxClass}
                        checked={selected.has(row.rowIndex)}
                        onChange={(e) => {
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(row.rowIndex);
                            else next.delete(row.rowIndex);
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm text-ink">
                        {row.category ?? row.party ?? row.type}
                      </span>
                      {row.duplicateOfTransactionId !== undefined && (
                        <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                          Possible duplicate
                        </span>
                      )}
                    </label>
                    <Amount
                      currency="PKR"
                      minorUnits={row.type === 'expense' ? -row.amountMinor : row.amountMinor}
                    />
                  </>
                ) : (
                  <span className="text-sm text-danger">
                    Row {row.rowIndex + 1}: <strong>{row.error}</strong>
                  </span>
                )}
              </li>
            ))}
          </ul>

          <label className="mb-3 flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className={checkboxClass}
              checked={backfill}
              onChange={(e) => {
                setBackfill(e.target.checked);
              }}
            />
            This is historical data (backfill) — OB-3
          </label>

          <button
            type="button"
            className={primaryButtonInlineClass}
            disabled={selectedRows.length === 0 || commitMutation.isPending}
            onClick={runCommit}
          >
            Import {selectedRows.length} row{selectedRows.length === 1 ? '' : 's'}
          </button>
          {error !== null && <p className="mt-2 text-sm text-danger">{error}</p>}
        </div>
      )}

      {step === 4 && commitMutation.data !== undefined && (
        <div className="rounded-[var(--radius-card)] border border-border bg-surface p-6">
          <p className="text-sm text-ink">
            {commitMutation.data.created} transaction{commitMutation.data.created === 1 ? '' : 's'}{' '}
            created.
          </p>
          <button
            type="button"
            className={`${primaryButtonInlineClass} mt-3`}
            onClick={() => {
              navigate('/tx');
            }}
          >
            Review in Transactions
          </button>
        </div>
      )}
    </div>
  );
}
